class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("#predictive-search");
    this.isOpen = false;
    this.abortController = null;
    this.selectedIndex = -1;

    if (!this.input || !this.predictiveSearchResults) {
      console.error('PredictiveSearch: Required elements not found');
      return;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;

    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener("keydown", this.onKeydown.bind(this));
    
    this.input.addEventListener("focus", () => {
      if (this.input.value.trim().length > 0 && this.predictiveSearchResults.innerHTML) {
        this.open();
      }
    });

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) {
        this.close();
      }
    });

    this.input.form?.addEventListener("submit", (event) => {
      if (this.selectedIndex >= 0) {
        event.preventDefault();
        this.selectResult(this.selectedIndex);
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    const results = this.getAllResults();
    
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, results.length - 1);
        this.updateSelection(results);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection(results);
        break;
      case "Enter":
        if (this.selectedIndex >= 0) {
          event.preventDefault();
          this.selectResult(this.selectedIndex);
        }
        break;
      case "Escape":
        event.preventDefault();
        this.close();
        break;
    }
  }

  getAllResults() {
    return Array.from(this.predictiveSearchResults.querySelectorAll('.predictive-search-item a'));
  }

  updateSelection(results) {
    results.forEach((result, index) => {
      const item = result.closest('.predictive-search-item');
      if (index === this.selectedIndex) {
        item.classList.add('bg-gray-100');
        result.setAttribute('aria-selected', 'true');
        result.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        item.classList.remove('bg-gray-100');
        result.setAttribute('aria-selected', 'false');
      }
    });

    this.input.setAttribute('aria-activedescendant', 
      this.selectedIndex >= 0 ? `result-${this.selectedIndex}` : ''
    );
  }

  selectResult(index) {
    const results = this.getAllResults();
    if (results[index]) {
      window.location.href = results[index].href;
    }
  }

  getSearchResults(searchTerm) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.showLoading();

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection,article,page',
      'resources[limit]': '10',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
    });

    const url = `/search/suggest.json?${params.toString()}`;

    fetch(url, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.renderResults(data, searchTerm);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
          this.showError();
        }
      });
  }

  renderResults(data, searchTerm) {
    const resources = data.resources.results;
    const products = resources.products || [];
    const collections = resources.collections || [];
    const articles = resources.articles || [];
    const pages = resources.pages || [];
    
    const totalResults = products.length + collections.length + articles.length + pages.length;

    if (totalResults === 0) {
      this.predictiveSearchResults.innerHTML = `
        <div class="predictive-search-results bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden">
          <div class="p-6 text-center text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p class="text-body-base">Nenhum resultado encontrado para "<strong>${searchTerm}</strong>"</p>
            <p class="text-body-sm mt-2">Tente usar palavras-chave diferentes</p>
          </div>
        </div>
      `;
      this.open();
      return;
    }

    let html = '<div class="predictive-search-results bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden"><div class="max-h-[32rem] overflow-y-auto">';

    // Products
    if (products.length > 0) {
      html += `
        <div class="predictive-search-section border-b border-gray-100">
          <div class="px-4 py-3 bg-gray-50">
            <h3 class="text-body-sm font-semibold text-gray-700 uppercase tracking-wide">
              Produtos (${products.length})
            </h3>
          </div>
          <ul class="divide-y divide-gray-100" role="listbox">
      `;
      
      products.slice(0, 6).forEach(product => {
        const price = product.price2 ? (product.price2 / 1).toFixed(2).replace('.', ',') : '';
        const comparePrice = product.compare_at_price ? (product.compare_at_price / 100).toFixed(2).replace('.', ',') : '';
        
        html += `
          <li role="option" class="predictive-search-item hover:bg-gray-50 transition-colors">
            <a href="${product.url}" class="flex items-center gap-4 p-4 group">
              ${product.image ? `
                <div class="flex-shrink-0 w-16 h-16 bg-gray-100 rounded overflow-hidden">
                  <img src="${product.image}" alt="${product.title}" class="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy">
                </div>
              ` : ''}
              <div class="flex-1 min-w-0">
                <p class="text-body-base font-medium text-gray-900 truncate group-hover:text-primary">${product.title}</p>
                ${product.vendor ? `<p class="text-body-sm text-gray-500 truncate">${product.vendor}</p>` : ''}
                <div class="mt-1 flex items-center gap-2">
                  ${comparePrice && comparePrice !== price ? `<span class="text-body-sm text-gray-400 line-through">R$ ${comparePrice}</span>` : ''}
                  ${price ? `<span class="text-body-base font-semibold text-primary">R$ ${price}</span>` : ''}
                </div>
              </div>
              <svg class="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </li>
        `;
      });
      
      html += '</ul></div>';
    }

    // Collections
    if (collections.length > 0) {
      html += `
        <div class="predictive-search-section border-b border-gray-100">
          <div class="px-4 py-3 bg-gray-50">
            <h3 class="text-body-sm font-semibold text-gray-700 uppercase tracking-wide">
              Categorias (${collections.length})
            </h3>
          </div>
          <ul class="divide-y divide-gray-100" role="listbox">
      `;
      
      collections.slice(0, 4).forEach(collection => {
        html += `
          <li role="option" class="predictive-search-item hover:bg-gray-50 transition-colors">
            <a href="${collection.url}" class="flex items-center gap-4 p-4 group">
              ${collection.image ? `
                <div class="flex-shrink-0 w-12 h-12 bg-gray-100 rounded overflow-hidden">
                  <img src="${collection.image}" alt="${collection.title}" class="w-full h-full object-cover" loading="lazy">
                </div>
              ` : ''}
              <div class="flex-1 min-w-0">
                <p class="text-body-base font-medium text-gray-900 truncate group-hover:text-primary">${collection.title}</p>
              </div>
              <svg class="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </li>
        `;
      });
      
      html += '</ul></div>';
    }

    html += '</div>';
    
    // Footer with "View all results"
    html += `
      <div class="border-t border-gray-200 bg-gray-50 px-4 py-3">
        <a href="/search?q=${encodeURIComponent(searchTerm)}" class="flex items-center justify-center gap-2 text-body-base font-medium text-primary hover:text-secondary transition-colors">
          Ver todos os resultados para "${searchTerm}"
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </a>
      </div>
    `;
    
    html += '</div>';
    
    this.predictiveSearchResults.innerHTML = html;
    this.selectedIndex = -1;
    this.open();
    this.input.setAttribute('aria-expanded', 'true');
  }

  showLoading() {
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search-loading bg-white shadow-lg border border-gray-200 rounded-lg p-8">
        <div class="flex items-center justify-center gap-3">
          <svg class="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-gray-600">Buscando...</span>
        </div>
      </div>
    `;
    this.open();
  }

  showError() {
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search-error bg-white shadow-lg border border-red-200 rounded-lg p-6">
        <div class="flex items-center gap-3 text-red-600">
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Erro ao buscar resultados. Tente novamente.</span>
        </div>
      </div>
    `;
    this.open();
  }

  open() {
    this.predictiveSearchResults.style.display = "block";
    this.isOpen = true;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.predictiveSearchResults.style.display = "none";
    this.isOpen = false;
    this.selectedIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define("predictive-search", PredictiveSearch);
